import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Optional } from '@/generic/functional';
import {
  type TrackingIndicator,
  type TrackingToast,
  TrackingIndicatorComponent,
  TrackingToastComponent,
} from '@/generic/progress';
import { useRuleExtractionTracking } from '@/rules-management/primary/RuleExtractionTrackingContext.ts';

const useRuleExtractionTrackingOverlay = () => {
  const tracking = useRuleExtractionTracking();
  const navigate = useNavigate();
  const { t } = useTranslation('rules-management');

  const showIndicator = tracking.extracting && !tracking.visible;

  const goToImport = () => {
    navigate('/rules');
    tracking.openImport();
    tracking.dismissToast();
  };

  const indicator: Optional<TrackingIndicator> = showIndicator
    ? Optional.of({
        label: t('rules-management.tracking.indicator'),
        onClick: tracking.openImport,
      })
    : Optional.empty();

  const toast: Optional<TrackingToast> = tracking.toast.map((current) => ({
    kind: current.kind,
    title: t(`rules-management.tracking.toast.${current.kind}.title`),
    actionLabel: t(`rules-management.tracking.toast.${current.kind}.action`),
    onAction: goToImport,
    dismissLabel: t('rules-management.tracking.toast.dismiss'),
    onDismiss: tracking.dismissToast,
  }));

  return { indicator, toast };
};

export const RuleExtractionTrackingIndicatorComponent = () => {
  const { indicator } = useRuleExtractionTrackingOverlay();

  return (
    indicator
      .map((current) => (
        <TrackingIndicatorComponent
          key="indicator"
          label={current.label}
          onClick={current.onClick}
          dataSelector="rules-management.tracking-indicator"
        />
      ))
      .orUndefined() ?? null
  );
};

export const RuleExtractionTrackingToastComponent = () => {
  const { toast } = useRuleExtractionTrackingOverlay();

  return (
    toast
      .map((current) => (
        <TrackingToastComponent
          key="toast"
          toast={current}
          dataSelector="rules-management.tracking-toast"
        />
      ))
      .orUndefined() ?? null
  );
};
